import { useRef, useState } from "react";
import Image from "next/image";
import Popover from "../popover/popover";
import Emoji from "../emoji/emoji";
import ShareDialog from "../share/shareDialog";
import MarkersListIcon from "./markersListIcon";
import ConfirmationModal from "../confirmationModal/confirmationModal";
import { ConditionName, IMarker } from "interfaces";
import { useAppDispatch, useAppSelector } from "redux-share/hooks";
import {
    conditions,
    handleHasXTokenParamOrSendXToken,
    handlePopoverSizeInMiniPlayer,
    progressBar,
    responseValidator,
    secondsToHMS,
    videoTag
} from "helpers";
import useOnClickOutside from "helpers/hooks/useOnClickOutside";
import { setClearPopoversSlice, setMarkersListPopover, setPersonalMarkersPopover } from "redux-share/slices/popoversSlice";
import { getMarkerPosition } from "redux-share/actions/progressbar.actions";
import { handleVideoTimeline } from "redux-share/actions/controller.actions";
import { eventMarkerClickLog, sendFluentLog } from "../../../../../logs";
import { API, apiCaller } from "../../../../../services";
import { setMarkers } from "redux-share/slices/videoDataSlice";
import IMAGES from "../../../../../images/importImages";
import { setSelectedPersonalMarker } from "redux-share/slices/userSlice";
import ShareIcon from "../../icon-components/shareIcon";
import { shallowEqual } from "react-redux";
import { toast } from "mobin-toast";
import dynamic from "next/dynamic";
import ArrowUpIcon from "../../icon-components/arrowUpIcon";
import ArrowDownIcon from "../../icon-components/arrowDownIcon";

const ControllbarTooltip = dynamic(() => import("../../mainComponent/footer/controllbar/controllbarTooltip"));

const MarkersList = () => {
    const [openDialog, setOpenDialog] = useState<boolean>(false);
    const [videoCurrentTime, setVideoCurrentTime] = useState<number>(0);
    const [markerId, setMarkerId] = useState<number | undefined>(undefined);
    const [showTooltip, setShowTooltip] = useState<boolean>(false);
    const [openPersonalMarkerMenu, setOpenPersonalMarkerMenu] = useState<number | undefined>(undefined);
    const [deleteMarker, setDeleteMarker] = useState<IMarker | undefined>(undefined);

    const { markers, params } = useAppSelector(
        ({ videoData: { markers, params, isComplexPortrait, videoElementSizeInfo } }) => ({
            markers,
            params,
            isComplexPortrait,
            videoElementSizeInfo
        }),
        shallowEqual
    );
    const { markersListPopover } = useAppSelector(({ popovers: { markersListPopover } }) => ({ markersListPopover }), shallowEqual);
    useAppSelector(({ controller: { showMiniPlayer } }) => ({ showMiniPlayer }), shallowEqual);
    const markersListRef = useRef(null);

    const dispatch = useAppDispatch();

    const handleClickShare = (event: any, markerTime: number) => {
        event.preventDefault();
        event.stopPropagation();
        setOpenDialog(true);
        setVideoCurrentTime(markerTime);
        const result = markers.find((marker: any) => marker.time === markerTime)?.id;
        setMarkerId(result);
    };

    useOnClickOutside(markersListRef, () => {
        if (!conditions(ConditionName.isComplexPortrait)) dispatch(setMarkersListPopover(false));
    });

    const handleClickMarker = (markerTime: number, markerId: number, markerTitle: String, markerDescription: String, markerSticker: String) => {
        if (progressBar()) progressBar().style.width = `${getMarkerPosition(markerTime)}px`;
        handleVideoTimeline(videoTag().currentTime, markerTime);
        videoTag().currentTime = markerTime;
        if (!conditions(ConditionName.isComplexPortrait)) dispatch(setMarkersListPopover(false));
        sendFluentLog(eventMarkerClickLog(markerId, markerTime, markerTitle, markerDescription, markerSticker));
    };

    const handleShowMarkerListPopover = () => {
        if (conditions(ConditionName.isComplexPortrait)) dispatch(setClearPopoversSlice());
        dispatch(setMarkersListPopover(!markersListPopover));
    };

    const handleDeleteMarker = async (inputMarker: IMarker) => {
        if (conditions(ConditionName.trackerEnabled)) {
            const { status } = await apiCaller({
                url: API().user.deletePersonalMarker(inputMarker.isPersonal!),
                method: "DELETE",
                headers: handleHasXTokenParamOrSendXToken()
            });
            if (responseValidator(status)) {
                toast({ title: "نشانگر با موفقیت حذف شد.", type: "success" });
                dispatch(setMarkers(markers.filter((m: IMarker) => m.time !== inputMarker.time)));
            }
            setDeleteMarker(undefined);
        }
    };

    return (
        <div
            className={`pl-flex pl-items-center pl-p-1 pl-relative ${
                conditions(ConditionName.showMarkerListPopover) && "pl-bg-secondary-50/[0.4] pl-rounded-[4px]"
            }`}
            ref={conditions(ConditionName.isComplexPortrait) ? undefined : markersListRef}
        >
            <MarkersListIcon
                className={`pl-cursor-pointer ${
                    conditions(ConditionName.isComplexPortrait) && params.theme === "light" ? "pl-fill-gray-800" : "pl-fill-dark-5"
                }`}
                onClick={handleShowMarkerListPopover}
                onMouseOver={() => !conditions(ConditionName.isTouchScreen) && setShowTooltip(true)}
                onMouseLeave={() => !conditions(ConditionName.isTouchScreen) && setShowTooltip(false)}
            />
            {showTooltip && <ControllbarTooltip title="لیست نشانگرها (e)" className="!pl-bottom-[180%] !pl-right-[-48px]" />}
            <Popover
                classNames={`!pl-p-0 pl-left-0 !pl-bg-white dark:!pl-bg-dark-13 !pl-rounded-[4px] pl-mb-[33px]
                ${conditions(ConditionName.isFullMobilePortrait) && "!pl-w-screen"} ${
                    conditions(ConditionName.isMiniPlayer) && !conditions(ConditionName.isFullMobilePortrait)
                        ? `!pl-fixed pl-rounded-r-none pl-w-[250px] !pl-mb-0`
                        : "pl-w-[285px]"
                }`}
                animation={
                    conditions(ConditionName.isMiniPlayer) && !conditions(ConditionName.isFullMobilePortrait)
                        ? markersListPopover
                            ? "animate__fadeInLeft"
                            : "animate__fadeOutLeft"
                        : undefined
                }
                styles={handlePopoverSizeInMiniPlayer()}
                status={conditions(ConditionName.showMarkerListPopover)}
                onClose={() => {
                    dispatch(setMarkersListPopover(false));
                    setOpenPersonalMarkerMenu(undefined);
                }}
                ref={conditions(ConditionName.isComplexPortrait) ? markersListRef : undefined}
                isFullHeight={conditions(ConditionName.isComplexPortrait)}
                headerTitle="نشانگرها"
                isHeader={true}
                type={conditions(ConditionName.isComplexPortrait) ? "modal" : "popover"}
                isHasCatchUpLineOnTop={conditions(ConditionName.isComplexPortrait)}
            >
                <div className="pl-flex pl-flex-col pl-gap-6 pl-mx-2 pl-mt-2">
                    <div className="pl-flex pl-flex-col pl-gap-2 pl-pb-2">
                        {markers.map((marker: IMarker) => (
                            <div
                                key={marker.id}
                                onClick={() => handleClickMarker(marker.time, marker.id, marker.title, marker.description, marker.sticker)}
                                className="odd:dark:pl-bg-dark-1 odd:pl-bg-main/[0.1] hover:pl-bg-main/[0.1] hover:dark:bg-white/[0.25] pl-rounded-[8px]"
                            >
                                <div className="pl-flex pl-item-center pl-gap-2 pl-cursor-pointer pl-rounded-[4px] pl-relative pl-p-2 pl-mx-2">
                                    {marker.banner || !marker.sticker ? (
                                        <Image
                                            src={marker.banner ? marker.banner : IMAGES.defaultMarkersListItemIcon}
                                            width={40}
                                            height={40}
                                            alt={marker.title}
                                        />
                                    ) : (
                                        <div className="pl-w-[40px] pl-h-[40px] pl-bg-secondary dark:pl-bg-secondary-50 pl-rounded-[6px] pl-flex pl-items-center">
                                            <Emoji name={marker.sticker} className="pl-w-[20px] pl-h-[20px] pl-mx-auto" />
                                        </div>
                                    )}
                                    <div className="pl-text-gray-800 dark:pl-text-white">
                                        <p className="pl-flex pl-items-center pl-mb-1 pl-text-[14px] pl-font-bold pl-w-[130px] pl-whitespace-nowrap pl-overflow-hidden pl-text-ellipsis">
                                            <div>{marker.title}</div>
                                            {marker.isPersonal !== null && marker.isPersonal !== undefined && (
                                                <div className="pl-flex pl-items-center">
                                                    <Image
                                                        className="pl-w-full pl-h-full"
                                                        src={IMAGES.personalMarkerSign}
                                                        width={16}
                                                        height={16}
                                                        alt={marker.title}
                                                    />
                                                </div>
                                            )}
                                        </p>
                                        <p className="pl-text-[13px] pl-font-normal pl-w-[200px] pl-whitespace-nowrap pl-overflow-hidden pl-text-ellipsis">
                                            {marker.description}
                                        </p>
                                    </div>
                                    {marker.isPersonal !== null && marker.isPersonal !== undefined && (
                                        <div
                                            className="pl-absolute pl-left-0.5 pl-bottom-2.5"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                if (openPersonalMarkerMenu === marker.time) setOpenPersonalMarkerMenu(undefined);
                                                else setOpenPersonalMarkerMenu(marker.time);
                                            }}
                                        >
                                            {openPersonalMarkerMenu !== undefined && openPersonalMarkerMenu === marker.time ? (
                                                <ArrowUpIcon className="dark:pl-fill-z-secondary-50 pl-fill-secondary-500" />
                                            ) : (
                                                <ArrowDownIcon className="dark:pl-fill-z-secondary-50 pl-fill-secondary-500" />
                                            )}
                                        </div>
                                    )}
                                    <span
                                        className={`pl-absolute pl-left-9 pl-top-2 pl-text-[12px] pl-font-bold pl-bg-secondary/[0.3] dark:pl-bg-secondary/[0.6] pl-text-gray-800 dark:pl-text-white pl-rounded-[4px] pl-p-[3px]`}
                                    >
                                        {secondsToHMS(marker.time)}
                                    </span>
                                    <ShareIcon
                                        className={`pl-flex pl-absolute pl-left-1 pl-top-3 pl-items-center pl-w-15 pl-h-15 pl-fill-gray-800 dark:pl-fill-white`}
                                        onClick={(event) => handleClickShare(event, marker.time)}
                                    />
                                </div>
                                {openPersonalMarkerMenu !== undefined && openPersonalMarkerMenu === marker.time && (
                                    <div className="pl-flex pl-item-center pl-cursor-pointer pl-rounded-[4px] pl-relative pl-p-2 pl-mx-2 pl-flex-col pl-w-full">
                                        <hr className="pl-bg-secondary-300 pl-ml-2" />
                                        <div className="pl-flex pl-items-center pl-w-full pl-pt-2">
                                            <div
                                                className="pl-flex pl-gap-4 pl-items-center"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    dispatch(setSelectedPersonalMarker(marker));
                                                    dispatch(setPersonalMarkersPopover(true));
                                                    dispatch(setMarkersListPopover(false));
                                                }}
                                            >
                                                <div className="pl-p-3 pl-bg-main-light pl-rounded-[8px] pl-flex pl-items-center">
                                                    <Image
                                                        className="pl-w-full pl-h-full"
                                                        src={IMAGES.editMarker}
                                                        width={16}
                                                        height={16}
                                                        alt={marker.title}
                                                    />
                                                </div>
                                                <span className="pl-text-gray-800 dark:pl-text-secondary-300 pl-text-[13px] pl-font-500">ویرایش</span>
                                            </div>
                                            <div
                                                className="pl-flex pl-flex-1 pl-justify-center pl-gap-4 pl-items-center"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    e.preventDefault();
                                                    setDeleteMarker(marker);
                                                    dispatch(setMarkersListPopover(false));
                                                }}
                                            >
                                                <div className="pl-p-3 pl-bg-main-light pl-rounded-[8px] pl-flex pl-items-center">
                                                    <Image
                                                        className="pl-w-full pl-h-full"
                                                        src={IMAGES.trashMarker}
                                                        width={16}
                                                        height={16}
                                                        alt={marker.title}
                                                    />
                                                </div>
                                                <span className="pl-text-gray-800 dark:pl-text-secondary-300 pl-text-[13px] pl-font-500">حذف</span>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </Popover>
            <ShareDialog currentTime={videoCurrentTime} status={openDialog} onClose={setOpenDialog} markerId={markerId} />
            <ConfirmationModal
                onClose={() => setDeleteMarker(undefined)}
                modalType={"DELETE"}
                status={!!deleteMarker}
                text={"آیا از حذف نشانگر مطمئن هستید؟"}
                cancelButtonText={"انصراف"}
                submitButtonText={"حذف"}
                onSubmit={() => handleDeleteMarker(deleteMarker!)}
            >
                <div className="pl-flex pl-flex-col pl-gap-1 pl-text-[16px] pl-font-medium pl-items-center pl-justify-between dark:pl-text-white pl-text-secondary-800 pl-my-4">
                    <div className="pl-relative pl-flex pl-flex-col pl-justify-center pl-items-center pl-self-center pl-shrink-0 pl-border pl-border-[1.5px] pl-rounded-[12px] pl-bg-main/[0.1] pl-w-[126px] pl-h-[110px]">
                        <span className="pl-emoji-mart-emoji pl-flex pl-flex-col pl-items-center pl-gap-[11px] pl-justify-center pl-h-full">
                            <Emoji name={deleteMarker?.sticker} className="pl-w-[56px] pl-h-[56px] pl-mx-auto" />
                        </span>
                    </div>
                    <div>{deleteMarker?.title}</div>
                </div>
            </ConfirmationModal>
        </div>
    );
};

export default MarkersList;
